import { useMemo } from "react";
import { ValueFormatterParams } from "ag-grid-community";
import { GetBillingScheduleResult, TorPinnedRow } from "./api";
import { DecimalUtils, formatCurrency, formatDateByType } from "../../../../../libs/utils";

/**
 * =========================
 * TOTALS TYPE
 * =========================
 */
export interface TorTotals {
  baseRent: string;
  saleAmount: string;
  torRent: string;
  difference: string;
}

/**
 * =========================
 * HOOK PARAMS
 * =========================
 */

interface UseTorGridParams {
  torScheduleData: GetBillingScheduleResult["torschedule"];
}

/**
 * =========================
 * HOOK
 * =========================
 */
export const useTorGrid = ({ torScheduleData }: UseTorGridParams) => {
  /**
   * =========================
   * TOTALS (single reduce pass)
   * =========================
   */
  const totals: TorTotals = useMemo(() => {
    const rows = torScheduleData || [];
    return {
      baseRent: DecimalUtils.add(rows.map((r) => r.baseRent)),
      saleAmount: DecimalUtils.add(rows.map((r) => r.saleAmount)),
      torRent: DecimalUtils.add(rows.map((r) => r.torRent)),
      difference: DecimalUtils.add(rows.map((r) => r.difference)),
    };
  }, [torScheduleData]);

  /**
   * =========================
   * PINNED ROW
   * =========================
   */
  const pinnedBottomRowData: TorPinnedRow[] = useMemo(
    () => [
      {
        startDate: "TOTAL",
        endDate: "",
        dueDate: "",
        baseRent: totals.baseRent,
        saleAmount: totals.saleAmount,
        torRent: totals.torRent,
        difference: totals.difference,
      },
    ],
    [totals],
  );

  /**
   * =========================
   * COLUMN DEFS
   * =========================
   */
  const columnDefs = useMemo(() => {
    return [
      {
        id: "startDate",
        field: "startDate",
        headerName: "From",
        flex: 1,
        minWidth: 150,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.node?.rowPinned) return params.value;
          return formatDateByType(params.value);
        },
        cellStyle: (params: any) => (params.node?.rowPinned ? { fontWeight: "bold" } : undefined),
      },
      {
        id: "endDate",
        field: "endDate",
        headerName: "To",
        flex: 1,
        minWidth: 150,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.node?.rowPinned) return "";
          return formatDateByType(params?.value) || "";
        },
      },
      {
        id: "dueDate",
        field: "dueDate",
        headerName: "Due Date",
        flex: 1,
        minWidth: 150,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.node?.rowPinned) return "";
          return formatDateByType(params?.value) || "";
        },
      },
      {
        id: "baseRent",
        field: "baseRent",
        headerName: "Base Rent",
        flex: 1,
        minWidth: 150,
        valueFormatter: ({ value }: ValueFormatterParams) => formatCurrency(value) || "",
      },
      {
        id: "saleAmount",
        field: "saleAmount",
        headerName: "Sale Amount",
        flex: 1,
        minWidth: 150,
        valueFormatter: ({ value }: ValueFormatterParams) => formatCurrency(value) || "",
      },
      {
        id: "torRent",
        field: "torRent",
        headerName: "TOR Calculation",
        flex: 1,
        minWidth: 150,
        valueFormatter: ({ value }: ValueFormatterParams) => formatCurrency(value) || "",
      },
      {
        id: "difference",
        field: "difference",
        headerName: "Difference",
        flex: 1,
        minWidth: 150,
        valueFormatter: ({ value }: ValueFormatterParams) => formatCurrency(value),
      },
    ];
  }, [formatDateByType, formatCurrency]);

  /**
   * =========================
   * RETURN
   * =========================
   */
  return {
    columnDefs,
    pinnedBottomRowData,
  };
};
